import { type RefObject, useEffect, useState } from 'react';

const THRESHOLD = 64; // px pulled before a refresh fires
const MAX_PULL = 96; // clamp the visual pull distance
const RESISTANCE = 0.5; // dampen the drag so it feels elastic

/**
 * Lightweight pull-to-refresh for the mobile scroll container. Only engages when
 * the container is scrolled to the very top and the gesture is a downward drag,
 * so it never fights native momentum scrolling.
 */
export function usePullToRefresh(
  ref: RefObject<HTMLElement | null>,
  onRefresh: () => void,
  // Bump this (e.g. the current route key) whenever the target element is
  // remounted, so the listeners re-attach to the fresh node.
  resetKey?: string | number,
): { pull: number; refreshing: boolean } {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    // resetKey carries no value of its own: reading it here is what re-runs the
    // effect, so the listeners re-attach to the node that replaced the old one.
    void resetKey;
    const el = ref.current;
    if (!el) return;

    let startY = 0;
    let active = false;
    let distance = 0;

    const onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (el.scrollTop <= 0 && e.touches.length === 1 && t) {
        startY = t.clientY;
        active = true;
        distance = 0;
      } else {
        active = false;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!active || !t) return;
      const dy = t.clientY - startY;
      if (dy <= 0 || el.scrollTop > 0) {
        active = false;
        setPull(0);
        return;
      }
      distance = Math.min(MAX_PULL, dy * RESISTANCE);
      setPull(distance);
      // We are consuming the gesture — prevent the rubber-band scroll.
      if (e.cancelable) e.preventDefault();
    };

    const onTouchEnd = () => {
      if (!active) return;
      active = false;
      if (distance >= THRESHOLD) {
        setRefreshing(true);
        setPull(THRESHOLD);
        onRefresh();
        window.setTimeout(() => {
          setRefreshing(false);
          setPull(0);
        }, 700);
      } else {
        setPull(0);
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [ref, onRefresh, resetKey]);

  return { pull, refreshing };
}
